import axios from 'axios'
import { Message } from 'element-ui'
import OSS from 'ali-oss'
import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'
import { to } from './http'
import { getSTokens, mergeFileV2, uploadFileV2, deleteFileV2 } from './apis'
import { decryptData, randUUID, fileExt } from './tool'

//是否开启oss文件直传, 默认是通过域名判断，如果域名是ip则关闭, 开发调试阶段直接开启，为：true
const isDev = process.env.NODE_ENV === 'development'
const isOss = isDev ? true : !/\d+\.\d+\.\d+\.\d+/.test(location.host)
const devDir = isDev ? 'env/' : ''
const vDir = 'v1/' + devDir
const Bucket = 'yun-platform-v1'
const Endpoint = 'yun-platform-v1.oss-cn-hangzhou.aliyuncs.com'
let pendingPromise = null

const fetchOss = async () => {
    const expire = localStorage.getItem('expire') ?? 0
    if (expire && Number(expire) > Date.parse(new Date()) / 1000) {
        return decryptData(localStorage.getItem('oss'))
    }
    if (pendingPromise) {
        return pendingPromise
    }
    pendingPromise = (async () => {
        try {
            const { code, msg, data } = await getSTokens()
            if (code === 200) {
                localStorage.setItem('oss', data.encoder)
                localStorage.setItem('expire', data.expire)
                return decryptData(data.encoder)
            }
        } catch (e) {
            Message.error(e.message ?? 'oss获取凭证异常')
            console.log('error', e)
        } finally {
            pendingPromise = null
        }
    })()
    return pendingPromise
}

const ossClient = async () => {
    const result = await fetchOss()
    if (result) {
        return new OSS({
            region: 'oss-cn-hangzhou',
            authorizationV4: true,
            endpoint: Endpoint,
            accessKeyId: result.accessKeyId,
            accessKeySecret: result.accessSecret,
            stsToken: result.securityToken,
            bucket: Bucket,
            cname: true,
            refreshSTSTokenInterval: 60000,
            refreshSTSToken: async () => {
                const result = await fetchOss()
                return {
                    accessKeyId: result.accessKeyId,
                    accessKeySecret: result.accessSecret,
                    stsToken: result.securityToken
                }
            }
        })
    }
}

// 上传文件
const uploadFileFn = async (file, callback, read = false) => {
    if (isOss) {
        const file_ext = fileExt(file)
        const filePath = `uploads/${vDir}${file_ext}/${moment().format('YYYYMMDD')}/${uuidv4()}.${file_ext}`
        const client = await ossClient()
        const options = {
            progress: (p, cpt, res) => {
                callback(p, cpt, client)
            },
            // 设置并发上传的分片数量。
            parallel: 4,
            // 设置分片大小。默认值为1 MB，最小值为100 KB，最大值为5 GB。最后一个分片的大小允许小于100 KB。
            partSize: 1024 * 1024
            // headers,
            // 自定义元数据，通过HeadObject接口可以获取Object的元数据。
            // meta: { "Content-Disposition": "inline" },
            // mime: "text/plain",
        }
        try {
            const _result = await client.multipartUpload(filePath, file, { ...options })
            if (_result.res && _result.res.statusCode === 200) {
                if (read) {
                    //设置公共读
                    await client.putACL(filePath, 'public-read')
                }
                return { filename: file.name, path: filePath, file: file }
            }
        } catch (e) {
            console.log('error', e)
            callback(0, null, client)
        }
    } else {
        const abortController = new AbortController()
        const CHUNK_SIZE = 1024 * 1024
        const total = Math.ceil(file.size / CHUNK_SIZE)
        const fileId = randUUID(16)
        for (let i = 0; i < total; i++) {
            const start = i * CHUNK_SIZE
            const end = Math.min(file.size, start + CHUNK_SIZE)
            const chunk = file.slice(start, end)

            const formData = new FormData()
            formData.append('file', chunk)
            formData.append('index', i)
            formData.append('filename', file.name)
            formData.append('file_id', fileId)
            formData.append('total', total)
            const [err, data] = await to(
                uploadFileV2(formData, {
                    signal: abortController.signal,
                    onUploadProgress: e => {
                        const current = (i + e.loaded / e.total) / total
                        callback(current, abortController, null)
                    }
                })
            )
            if (err) {
                axios.isCancel(err)
                callback(0, null, null)
                return false
            }
        }
        const [_err, data] = await to(mergeFileV2({ fileId: fileId, filename: file.name }))
        if (!_err && data) {
            return { filename: file.name, path: data.file_path, file: file }
        }
    }
    Message.error("上传失败")
    return false
}

// 返回预览链接，根据传入的文件路径，如：uploads/mp4/ada.mp4
const previewUrl = async path => {
    if (!isOss) return path
    const client = await ossClient()

    if (client) {
        return client.signatureUrl(path, { expire: 3600 })
    }
}

// 删除文件 path：文件路径
const deleteFile = async path => {
    if (isOss) {
        const client = await ossClient()
        const result = await client.delete(path)
    } else {
        await to(deleteFileV2({ path }))
    }
    return true
}

// 下载文件 path: 文件路径 ，filenam: 下载下来的文件名称要携带文件后缀
const downloadFile = async (path, name) => {
    if (!isOss) {
        const fileInfo = path.split("?")[0].split(".")
        const ext = fileInfo[fileInfo.length - 1]
        name = name.replace(`.${ext}`, ""); //文件名携带后缀则替换为空
        const filename = name === "" ? `${Date.now()}.${ext}` : `${name}.${ext}`;
        fetch(path).then(response => response.blob()).then(blob => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a")
            a.style.display = "none"
            a.href = url
            a.download = filename
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
        }).catch(() => console.log("failed"))
    }
    const client = await ossClient()
    if (client) {
        let val = 'attachment'
        if (name) {
            val += `; filename=${encodeURIComponent(name)}`
        }
        const response = {
            'content-disposition': val
        }
        const url = client.signatureUrl(path, { response })
        window.open(url, '_self')
    }
}

// 取消上传
const cancelUpload = async (abortController, client = null) => {
    if (isOss && client) {
        client.abortMultipartUpload(abortController.name, abortController.uploadId)
    } else {
        abortController && abortController.abort()
    }
}

const cropImg = async (processParam, path) => {
    let param = 'image/crop'
    for (const key in processParam) {
        console.log('key', key)
        param += `,${key}_${processParam[key]}`
    }

    if (isOss) {
        const client = await ossClient()
        const result = await client.processObjectSave(path, path, param, Bucket)
        return result.res.statusCode === 200
    }
}
export {
    fetchOss,
    ossClient,
    previewUrl,
    deleteFile,
    downloadFile,
    uploadFileFn,
    cancelUpload,
    cropImg
}
